import { ReactNode, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';

import { Chevron } from 'components/Chevron/Chevron';
import { Ordinal } from 'components/base/Ordinal';

import './DetailCard.scss';

interface DetailCardProps {
  /** 1-based position shown as an Ordinal badge in the header. Omit for an unnumbered card. */
  readonly index?: number;
  /** Header title. Omit together with everything else to get a headerless (plain surface) card. */
  readonly title?: ReactNode;
  /** Muted text shown next to the title. */
  readonly subtitle?: ReactNode;
  /** Show a chevron that collapses/expands the body. */
  readonly collapsible?: boolean;
  /** Start collapsed when `collapsible` (default: expanded). */
  readonly defaultCollapsed?: boolean;
  /** When set, a trash button in the header calls this. */
  readonly onRemove?: () => void;
  /** Disable the remove button (e.g. the last item can't be removed). */
  readonly removeDisabled?: boolean;
  /** Accessible label / tooltip for the remove button. */
  readonly removeLabel?: string;
  /** Extra header controls, placed before the remove button. */
  readonly actions?: ReactNode;
  /** Opaque drag-handle node injected by `DetailList` when the list is ordered. */
  readonly dragHandle?: ReactNode;
  /**
   * Explicit reorder controls (alongside drag). When either is set, up/down buttons render in the header; pass `undefined`
   * for the one at the boundary (first item -> no `onMoveUp`, last -> no `onMoveDown`) and it shows disabled.
   */
  readonly onMoveUp?: () => void;
  readonly onMoveDown?: () => void;
  readonly children: ReactNode;
}

/**
 * A card in the "details" shell: an optional header band (drag handle, collapse
 * chevron, ordinal, title/subtitle, actions, remove) over a body. Wraps the
 * react-bootstrap Card so surface/border/radius theming (light + dark) comes for
 * free. With no header-worthy props it renders as a plain headerless surface —
 * used for the top section of a card-stacked form.
 */
export const DetailCard = observer(
  ({
    index,
    title,
    subtitle,
    collapsible = false,
    defaultCollapsed = false,
    onRemove,
    removeDisabled = false,
    removeLabel = 'Remove',
    actions,
    dragHandle,
    onMoveUp,
    onMoveDown,
    children,
  }: DetailCardProps) => {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const hasTitle = title !== undefined || subtitle !== undefined;
    const reorderable = onMoveUp !== undefined || onMoveDown !== undefined;
    const hasHeader =
      dragHandle !== undefined || reorderable || index !== undefined || hasTitle || actions !== undefined || onRemove !== undefined || collapsible;

    return (
      <Card className="detail-card">
        {hasHeader && (
          <Card.Header className="detail-card__head">
            {dragHandle}
            {collapsible && <Chevron expanded={!collapsed} onToggle={() => setCollapsed((prev) => !prev)} />}
            {index !== undefined && <Ordinal value={index} />}
            {hasTitle && (
              <span className="detail-card__title">
                {title}
                {subtitle !== undefined && <small className="detail-card__subtitle text-muted">{subtitle}</small>}
              </span>
            )}
            <span className="detail-card__actions">
              {actions}
              {reorderable && (
                <span className="detail-card__reorder">
                  <Button
                    variant="link"
                    className="detail-card__move"
                    aria-label="Move up"
                    title="Move up"
                    onClick={onMoveUp}
                    disabled={onMoveUp === undefined}
                  >
                    <FontAwesomeIcon icon={faChevronUp} />
                  </Button>
                  <Button
                    variant="link"
                    className="detail-card__move"
                    aria-label="Move down"
                    title="Move down"
                    onClick={onMoveDown}
                    disabled={onMoveDown === undefined}
                  >
                    <FontAwesomeIcon icon={faChevronDown} />
                  </Button>
                </span>
              )}
              {onRemove !== undefined && (
                <Button
                  variant="link"
                  className="detail-card__remove"
                  aria-label={removeLabel}
                  title={removeLabel}
                  onClick={onRemove}
                  disabled={removeDisabled}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </span>
          </Card.Header>
        )}
        {(!collapsible || !collapsed) && <Card.Body>{children}</Card.Body>}
      </Card>
    );
  }
);
